import { t } from 'i18next';

import { PersistentStore } from '../stores/PersistentStore';
import { UIHelper } from './UIHelper';
import { ScreenshotHelper } from './ScreenshotHelper';
import { ToggleStoreState } from '../stores/ToggleStore';
import { ProjectManagerStore } from '../stores/ProjectManagerStore';
import { ArticleActions } from '../actions/ArticleActions';
import { PDFMaker } from './PDFMaker';
import { ArticleStore } from '../stores/ArticleStore';
import { Workbook, XLSXDocument } from './XLSXDocument';
import { DownloadHelper } from './DownloadHelper';
import { ExportGeometryFormat } from '../constants/ExportFormats';
import { DialogActions } from '../actions/DialogActions';
import { Stats } from './Stats';
import { UrlHelper } from '../utils/UrlHelper';
import { ExportMapping, linkInfo } from './MailHelper';

interface MailShareContent {
    planning?: string;
    cad?: Array<ExportMapping>;
    session?: Array<ExportMapping>;
    image?: string;
    articleList?: string;
    excel?: string;
}

export abstract class MailBrowserHelper {
    public static async generateMailShareContent(exportSettings: ToggleStoreState, killProcess: () => boolean): Promise<MailShareContent | null> {
        const filename: string = ProjectManagerStore.getProjectName(true);
        const shareContent: MailShareContent = {};
        let itemCount: number = 0;
        if (killProcess != null && killProcess()) {
            return null;
        }

        await ArticleActions.updateArticleList();

        // add Screenshot
        if (killProcess != null && killProcess()) {
            return null;
        }

        const pboxUrl: string = await DownloadHelper.exportPBox();
        shareContent.planning = UrlHelper.getCurrentLink({
            queryArguments: {
                pbox: pboxUrl
            },
            ignoreHashArguments: true,
            hashArguments: {}
        });

        // add Screenshot
        if (killProcess != null && killProcess()) {
            return null;
        }

        if (exportSettings.indexOf('IMAGE') !== -1) {
            shareContent.image = await DownloadHelper.uploadContentToFileStorage(
                await ScreenshotHelper.getScreenshot({
                    width: 1920,
                    height: 1080,
                    stripUrlScheme: true,
                    renderAsync: true,
                    addWatermark: true,
                    customMimetype: true
                }),
                filename,
                PersistentStore.getImageExtension()
            );
            itemCount++;
        }

        // add PDF Article List
        if (killProcess != null && killProcess()) {
            return null;
        }

        if (exportSettings.indexOf('PDF') !== -1) {
            shareContent.articleList = await DownloadHelper.uploadContentToFileStorage(
                await PDFMaker.generatePDFAsBase64(),
                filename,
                'pdf'
            );
            itemCount++;
        }

        // add Excel Article List
        if (killProcess != null && killProcess()) {
            return null;
        }

        if (exportSettings.indexOf('EXCEL') !== -1) {
            const xlsx: XLSXDocument = new XLSXDocument(ArticleStore.getState().articleList);
            const workbook: Workbook = await xlsx.getDocument(UIHelper.showPrices());
            shareContent.excel = await workbook.outputAsync('base64') as string;
            shareContent.excel = await DownloadHelper.uploadContentToFileStorage(
                await workbook.outputAsync('base64') as string,
                filename,
                'xlsx'
            );
            itemCount++;
        }

        // upload the image and article list if needed (if not in app mode)
        if (killProcess != null && killProcess()) {
            return null;
        }

        // generate the cad files
        const cadSetting: ToggleStoreState = exportSettings.filter((setting) => {
            return setting === '3DS' || setting === 'DWG' || setting === 'SKP' || setting === 'FBX' || setting === 'Revit';
        });

        if (cadSetting.length) {
            shareContent.cad = [];
            const exportSettingsArray: Array<Array<string>> = cadSetting.map((value: string): Array<string> => {
                return ExportGeometryFormat[value];
            });
            let exportedGeometries: Array<string> = [];

            if (killProcess != null && killProcess()) {
                return null;
            }

            try {
                exportedGeometries = await DownloadHelper.exportGeometries(exportSettingsArray);
            } catch (e) {
                let label: string = t('The CAD file could not be generated.');
                if (cadSetting.length > 1) {
                    label = t('The CAD files could not be generated.');
                }
                const continueProgress: boolean = await DialogActions.getPromiseDialog(label);
                if (!continueProgress) {
                    return null;
                }
            }

            for (let i: number = 0; i < exportedGeometries.length; i++) {
                if (killProcess != null && killProcess()) {
                    return null;
                }

                try {
                    shareContent.cad.push({
                        format: cadSetting[i],
                        url: await DownloadHelper.uploadUrlToFileStorage(exportedGeometries[i], filename)
                    });
                } catch (e) {
                    const continueProgress: boolean = await DialogActions.getPromiseDialog(
                        t('The CAD file could not be generated.') + ' (' + cadSetting[i] + ')'
                    );
                    if (!continueProgress) {
                        return null;
                    }
                }
            }
            itemCount += shareContent.cad.length;
        }

        // Generate the store the session export files
        const sessionExports: Array<ExportMapping> = [];

        if (killProcess != null && killProcess()) {
            return null;
        }

        if (exportSettings.indexOf('OBX') !== -1) {
            const obxUrl: string = await DownloadHelper.exportObx();
            sessionExports.push({
                format: 'OBX',
                url: await DownloadHelper.uploadUrlToFileStorage(obxUrl, filename)
            });
            itemCount++;
        }

        if (killProcess != null && killProcess()) {
            return null;
        }

        if (exportSettings.indexOf('OBK') !== -1) {
            const obkUrl: string = await DownloadHelper.exportObk();
            sessionExports.push({
                format: 'OBK',
                url: await DownloadHelper.uploadUrlToFileStorage(obkUrl, filename)
            });
            itemCount++;
        }
        shareContent.session = sessionExports;
        if (itemCount === 0) {
            return null;
        }
        return shareContent;
    }

    public static async getMailToLink(exportSettings: ToggleStoreState, killProcess: () => boolean): Promise<string | null> {
        const shareContent: MailShareContent | null = await this.generateMailShareContent(exportSettings, killProcess);
        if (shareContent == null) {
            return null;
        }
        let shareLinks: Array<ExportMapping> = [];
        if (shareContent.cad != null) {
            shareLinks = shareLinks.concat(shareContent.cad);
        }

        if (shareContent.session != null) {
            shareLinks = shareLinks.concat(shareContent.session);
        }

        // send statistic data
        shareLinks.map((value: ExportMapping): void => {
            Stats.sendEvent({
                category: 'Send',
                action: 'e-mail',
                label: value.format
            });
        });

        if (shareContent.articleList != null) {
            Stats.sendEvent({
                category: 'Send',
                action: 'e-mail',
                label: 'PDF'
            });
        }

        if (shareContent.image != null) {
            Stats.sendEvent({
                category: 'Send',
                action: 'e-mail',
                label: PersistentStore.getState().imageFormat
            });
        }

        return getTextEmail(shareContent, shareLinks);
    }
}

async function getTextEmail(shareContent: MailShareContent, shareLinks: Array<ExportMapping>): Promise<string> {
    let body: string = '';
    let linkInfoNeeded: boolean = false;

    if (shareContent.planning != null) {
        linkInfoNeeded = true;
        body += '\r\n' + t('3D configuration') + ': ' + shareContent.planning + '\r\n';
    }

    if (shareContent.image != null) {
        linkInfoNeeded = true;
        body += '\r\n' + t('Image') + ': ' + shareContent.image + '\r\n';
    }

    if (shareContent.articleList != null) {
        linkInfoNeeded = true;
        body += '\r\n' + t('Article List') + ' (PDF): ' + shareContent.articleList + '\r\n';
    }

    if (shareContent.excel != null) {
        linkInfoNeeded = true;
        body += '\r\n' + t('Article List') + ' (Excel): ' + shareContent.excel + '\r\n';
    }

    const infoLinks: Array<ExportMapping> = shareLinks.filter((shareLink: ExportMapping) => {
        return (
            shareLink.format === 'OBK' ||
            shareLink.format === 'OBX'
        );
    });

    const cadLinks: Array<ExportMapping> = shareLinks.filter((shareLink: ExportMapping) => {
        return !(
            shareLink.format === 'OBK' ||
            shareLink.format === 'OBX'
        );
    });

    if (infoLinks.length) {
        linkInfoNeeded = true;
        body += '\r\n' + t('Commercial Information') + ':';
        body += '\r\n' + infoLinks.map((value: ExportMapping): string => {
            return value.format + ': ' + value.url;
        }).join('\r\n') + '\r\n';
    }

    if (cadLinks.length) {
        linkInfoNeeded = true;
        body += '\r\n' + t('CAD Symbols') + ':';
        body += '\r\n' + cadLinks.map((value: ExportMapping): string => {
            return value.format + ': ' + value.url;
        }).join('\r\n');
    }

    body += '\r\n\r\n';
    body += t('You can find the configurator here: {{- url}}', {url: 'https://configurator.bpro-solutions.com'});
    body += '\r\n';
    body += t('For more information about B.PRO go to: {{- url}}', {url: 'https://www.bpro-solutions.com'});
    body += '\r\n\r\n\r\n';
    body += t('With best regards,');
    body += '\r\n';

    if (linkInfoNeeded) {
        body = linkInfo() + body;
    }

    const subject: string = t('3D configuration with products from B.PRO');
    return 'mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/MailBrowserHelper.ts